import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router';
import { Button, Card } from '../../ui';
import { getMyClientRow, getSession } from '../../shared/queries';
import { supabase } from '../../shared/supabase/client';
import { useInstance } from '../instance/InstanceProvider';
import { text } from '../../shared/text';
import { ClientGuard } from './ClientGuard';
import { AppBar, ClientLoading, ClientNotFound, ClientPage } from './ClientPage';
import { weekParam, weekQuery } from './origin';
import { completeSession, EFFORT_MAX, EFFORT_MIN, FEELINGS, useSessionProgress } from './progress';
import { hasConsent, useChoices } from './consentChoices';
import type { Feeling } from './progress';

/**
 * The thirty seconds that end a session.
 *
 * ## What it asks, and what it must never ask
 *
 * Two questions, both required, both answerable by tapping: how hard it was, and
 * how it felt. A third — whether anything hurt — appears **only** for a client
 * who opted in to being asked it, and is answerable only yes or no.
 *
 * There is no body-area picker and no pain-description field on this screen, in
 * this file, or anywhere in the catalogue of words it draws from, and there must
 * never be one. `mvp-discovery` → `client-app` forbids asking where it hurt or
 * for any description of pain, and the reason is not squeamishness: the person
 * who reads the answer is a personal trainer who is also a **medical student**,
 * not a physician, and a product that collects symptom reports for her to read
 * has quietly made her into something she is not qualified to be. So the answer
 * to "did something hurt" is a flag, the response to it is to stop and tell a
 * human, and the human named for anything medical is the client's **own**
 * doctor.
 *
 * ## It can be finished without typing
 *
 * Everything required is a tap. The free-text box is optional, is offered to
 * every client whether or not they opted in to the hurt question — so nobody is
 * left with no way to say something — and is capped at 200 characters, because
 * it is a note, not a consultation.
 *
 * ## What actually happens when it is submitted
 *
 * **Nothing leaves the device.** There is no `check_in` table, no request, and
 * no notification: `completeSession` writes to this browser's own storage and
 * that is the end of it. The confirmation says the coach will see it because
 * that is the sentence the specification asks for and because it is what will be
 * true — but it is not true yet, and `./progress.ts` says so at length. This is
 * the hollow half of `client-daily-loop`, named in its own proposal, and it
 * stays hollow until the coach's side of the check-in is built.
 */
export function CheckInPage() {
  return (
    <ClientGuard>
      <CheckIn />
    </ClientGuard>
  );
}

function CheckIn() {
  const instance = useInstance();
  const params = useParams();
  const [search] = useSearchParams();
  const sessionId = params['sessionId'] ?? '';
  const progress = useSessionProgress(sessionId);

  /*
   * The week this was started from, carried through from the session so that
   * finishing a session the client went back for lands them where they were
   * rather than on Today. See `./origin.ts`.
   */
  const fromWeek = weekParam(search);

  const session = useQuery({
    queryKey: ['session', sessionId],
    queryFn: () => getSession(supabase, sessionId),
  });

  /*
   * The one consent this screen reads. A client who never opted in is never
   * shown the question — not a disabled one, not a hidden one, not one that
   * appears after they answer something else. While the row is still loading the
   * question is absent too, which is the safe direction to be wrong in.
   */
  const me = useQuery({ queryKey: ['my-client-row'], queryFn: () => getMyClientRow(supabase) });
  const choices = useChoices();
  /* The row, overlaid by this device's own choice — see `MessagesPage.tsx`. */
  const askAboutHurt = hasConsent('hurt', me.data, choices);

  /*
   * Whether the offer to tell her about it can be made at all.
   *
   * A client who has not consented to stored messages has no thread, so the
   * offer would lead to a screen explaining that messaging is off — which is the
   * wrong thing to put in front of somebody who has just said something hurt.
   * They still get the safety message, and it already names both the person who
   * trains them and their own doctor, so the route out is not lost to a privacy
   * choice; it is simply not a thread (`client-coach-presence`, "The client who
   * declined messaging still gets a route"). **Nothing is written either way.**
   */
  const canMessage = hasConsent('messaging', me.data, choices);

  const [effort, setEffort] = useState<number | null>(null);
  const [feeling, setFeeling] = useState<Feeling | null>(null);
  const [hurt, setHurt] = useState(false);
  const [note, setNote] = useState('');
  const [missing, setMissing] = useState(false);

  if (session.isPending) return <ClientLoading />;
  if (!session.data) return <ClientNotFound />;

  if (progress.completedAt !== null) {
    return (
      <Confirmation
        slug={instance.slug}
        coach={instance.coachDisplayName}
        sessionId={sessionId}
        hurt={progress.checkIn?.hurt === true}
        canMessage={canMessage}
        fromWeek={fromWeek}
      />
    );
  }

  const submit = () => {
    if (effort === null || feeling === null) {
      setMissing(true);
      return;
    }
    completeSession(sessionId, {
      effort,
      feeling,
      hurt: askAboutHurt ? hurt : null,
      note: note.trim(),
      at: new Date().toISOString(),
    });
  };

  return (
    <ClientPage>
      <AppBar
        backTo={`/c/${instance.slug}/sessions/${sessionId}${weekQuery(fromWeek)}`}
        backLabel={text('client.checkIn.back')}
      />

      <h1 className="text-ink font-display mt-2 text-3xl leading-tight">
        {text('client.checkIn.title')}
      </h1>
      <p className="text-ink-muted mt-1 text-sm">{text('client.checkIn.intro')}</p>

      {/* --- how hard was it ------------------------------------------- */}
      <fieldset className="mt-6 border-0 p-0">
        <legend className="text-ink text-sm font-medium">{text('client.checkIn.effort')}</legend>
        <p className="text-ink-muted mt-1 text-xs">{text('client.checkIn.effortHint')}</p>

        <div className="mt-2 grid grid-cols-5 gap-2">
          {efforts().map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={effort === value}
              aria-label={text('client.checkIn.effortOption', { value })}
              onClick={() => {
                setEffort(value);
                setMissing(false);
              }}
              className={chip(effort === value)}
            >
              {value}
            </button>
          ))}
        </div>
      </fieldset>

      {/* --- how did it feel ------------------------------------------- */}
      <fieldset className="mt-6 border-0 p-0">
        <legend className="text-ink text-sm font-medium">{text('client.checkIn.feeling')}</legend>

        <div className="mt-2 flex flex-wrap gap-2">
          {FEELINGS.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={feeling === value}
              onClick={() => {
                setFeeling(value);
                setMissing(false);
              }}
              className={chip(feeling === value, 'px-4')}
            >
              {text(FEELING_TEXT[value])}
            </button>
          ))}
        </div>
      </fieldset>

      {/* --- did anything hurt, only where it was consented to ---------- */}
      {askAboutHurt ? (
        <fieldset className="mt-6 border-0 p-0">
          <legend className="text-ink text-sm font-medium">{text('client.checkIn.hurt')}</legend>

          <div className="mt-2 flex gap-2">
            <button
              type="button"
              aria-pressed={!hurt}
              onClick={() => {
                setHurt(false);
              }}
              className={chip(!hurt, 'px-6')}
            >
              {text('client.checkIn.hurtNo')}
            </button>
            <button
              type="button"
              aria-pressed={hurt}
              onClick={() => {
                setHurt(true);
              }}
              className={chip(hurt, 'px-6')}
            >
              {text('client.checkIn.hurtYes')}
            </button>
          </div>

          {/*
            The safety message appears the instant the answer is yes — not after
            submitting, not on the next screen. It is carried by the attention
            colour and by its words, never by colour alone, and it is not the
            removal colour: amber means a person needs attention, red means
            something is being destroyed (`design-system`).
          */}
          {hurt ? (
            <SafetyMessage
              slug={instance.slug}
              sessionId={sessionId}
              coach={instance.coachDisplayName}
              canMessage={canMessage}
            />
          ) : null}
        </fieldset>
      ) : null}

      {/* --- anything else, always offered, never required -------------- */}
      <div className="mt-6 flex flex-col gap-1.5">
        <label htmlFor="check-in-note" className="text-ink text-sm font-medium">
          {text('client.checkIn.note')}
        </label>
        <textarea
          id="check-in-note"
          value={note}
          maxLength={200}
          rows={3}
          placeholder={text('client.checkIn.notePlaceholder')}
          onChange={(event) => {
            setNote(event.target.value);
          }}
          aria-describedby="check-in-note-hint"
          className="bg-surface-raised text-ink border-border-strong rounded-[--radius-control] border px-3 py-2 text-base"
        />
        <p id="check-in-note-hint" className="text-ink-muted text-xs">
          {text('client.checkIn.noteHint', { coach: instance.coachDisplayName })}
        </p>
      </div>

      {missing ? (
        <p role="alert" className="text-danger mt-4 text-sm">
          {text('client.checkIn.missing')}
        </p>
      ) : null}

      <Button variant="accent" size="lg" className="mt-6" onClick={submit}>
        {text('client.checkIn.submit')}
      </Button>
    </ClientPage>
  );
}

/**
 * The safety message, and the offer that makes its last sentence true.
 *
 * It appears the instant the answer is yes — not after submitting, not on the
 * next screen — and again on the confirmation, so that a client who submits
 * does not lose the route. It is carried by the attention colour and by its
 * words, never by colour alone, and it is not the removal colour: amber means a
 * person needs attention, red means something is being destroyed
 * (`design-system`).
 *
 * **The offer opens the thread and does nothing else.** It carries a reference
 * to the session in the address, it prefills no text, and it sends nothing: the
 * client is free to write as much or as little as they want, including nothing,
 * and to walk away from the screen entirely. Nothing on this path asks where it
 * hurt, how much, or for how long, and there is nowhere in any shape it touches
 * to record an answer if it did.
 *
 *   client-coach-presence, "Saying that something hurt leads to a person"
 */
function SafetyMessage({
  slug,
  sessionId,
  coach,
  canMessage,
}: {
  slug: string;
  sessionId: string;
  coach: string;
  canMessage: boolean;
}) {
  return (
    <div
      role="status"
      className="bg-attention-tint mt-3 rounded-[--radius-card] p-3 text-sm leading-relaxed"
    >
      <p className="text-attention">{text('client.checkIn.hurtSafety', { coach })}</p>

      {canMessage ? (
        <Link
          to={`/c/${slug}/messages?re=${sessionId}`}
          className="text-primary mt-2 inline-flex min-h-11 items-center font-medium underline-offset-2 hover:underline"
        >
          {text('client.checkIn.hurtMessage', { coach })}
        </Link>
      ) : null}
    </div>
  );
}

/**
 * Done, and nothing more than done.
 *
 * It awards nothing, announces no total, counts no run of days and compares the
 * session to no earlier one (`client-tone`, "Completing a session is
 * acknowledged once and modestly"). Two sentences and the way back.
 *
 * The one exception is a check-in that said something hurt: the safety message
 * is repeated here with the offer beside it, because the inline one disappeared
 * when the check-in was submitted and losing the route at exactly that moment is
 * the failure this slice exists to fix.
 */
function Confirmation({
  slug,
  coach,
  sessionId,
  hurt,
  canMessage,
  fromWeek,
}: {
  slug: string;
  coach: string;
  sessionId: string;
  hurt: boolean;
  canMessage: boolean;
  /** The week this session was opened from, or `null` for one opened from Today. */
  fromWeek: number | null;
}) {
  return (
    <ClientPage>
      <Card tone="soft" className="mt-8">
        <h1 className="text-ink font-display text-2xl">{text('client.checkIn.doneTitle')}</h1>
        <p className="text-ink-soft mt-2 text-sm">{text('client.checkIn.doneBody', { coach })}</p>
      </Card>

      {hurt ? (
        <SafetyMessage slug={slug} sessionId={sessionId} coach={coach} canMessage={canMessage} />
      ) : null}

      {/*
        Back to wherever this started. A session completed late, from a week the
        client went back to on purpose, returns to that week — where the row they
        came from now reads "Hecha". Coming out onto Today instead would lose the
        one thing they had just done.
      */}
      <Link
        to={fromWeek === null ? `/c/${slug}/today` : `/c/${slug}/week/${String(fromWeek)}`}
        className="text-primary mt-6 inline-flex min-h-11 items-center text-sm font-medium underline-offset-2 hover:underline"
      >
        {fromWeek === null ? text('client.checkIn.doneBack') : text('client.week.backToWeek')}
      </Link>
    </ClientPage>
  );
}

/** Which word each of the five answers is. Named, so a sixth cannot appear. */
const FEELING_TEXT = {
  great: 'client.checkIn.feelingGreat',
  good: 'client.checkIn.feelingGood',
  ok: 'client.checkIn.feelingOk',
  tough: 'client.checkIn.feelingTough',
  rough: 'client.checkIn.feelingRough',
} as const satisfies Record<Feeling, string>;

function efforts(): number[] {
  return Array.from({ length: EFFORT_MAX - EFFORT_MIN + 1 }, (_, index) => EFFORT_MIN + index);
}

/**
 * A tappable answer: 44 px high, legible whether chosen or not.
 *
 * Chosen is the deep green with white on it at 8.27:1; unchosen is a white card
 * with ink on it at 18.84:1 and a `border-strong` edge at 3.62:1, which is the
 * only thing that says it is a control. Neither state depends on the neon, which
 * is a fill for one button on Today and not a palette for everything tappable.
 */
function chip(selected: boolean, extra = 'px-3'): string {
  return [
    'inline-flex min-h-11 items-center justify-center rounded-full border text-sm font-medium transition-colors',
    extra,
    selected
      ? 'bg-primary text-on-primary border-primary'
      : 'bg-surface-raised text-ink border-border-strong',
  ].join(' ');
}
